import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import { API_BASE_URL } from '../config/api';
import './Reports.css';

type ReportDefinition = {
  type: string;
  title: string;
  description: string;
  accent: string;
  formats: string[];
};

type ReportFilters = {
  preset: string;
  start_date?: string;
  end_date?: string;
  asset_class: string;
  investment_type: string;
  account_id: string;
  instrument: string;
  include_closed: boolean;
};

type Snapshot = {
  report_type: string;
  title: string;
  generated_at: string;
  filters: ReportFilters;
  summary: Record<string, any>;
  monthly: Array<Record<string, any>>;
  allocation: Array<Record<string, any>>;
  subcategories: Array<Record<string, any>>;
  contributions_by_type: Array<Record<string, any>>;
  reconciliation: { matched: boolean; contribution_difference: number; current_value_difference: number };
  holdings: Array<Record<string, any>>;
  transactions: Array<Record<string, any>>;
  coverage: { level: string; missing: string[]; warnings: string[]; unclassified_value: number; message: string };
  disclaimer: string;
};

type GeneratedReport = {
  id: string;
  report_type: string;
  title: string;
  status: string;
  progress: number;
  error_message: string;
  created_at: string;
  expires_at: string;
  available_formats: string[];
};

const initialFilters: ReportFilters = {
  preset: 'current_fy',
  asset_class: 'all',
  investment_type: 'all',
  account_id: 'all',
  instrument: '',
  include_closed: false,
};

const COLORS = ['#0d7c66', '#ed6a3d', '#e3aa24', '#2878a5', '#8a5a44', '#718096', '#b84a4a'];

const INVESTMENT_TYPES = [
  ['all', 'All investment types'],
  ['direct_stocks', 'Direct stocks / broker portfolio'],
  ['mutual_funds', 'Mutual funds'],
  ['epf', 'EPF'],
  ['ppf', 'PPF'],
  ['lic', 'LIC / insurance'],
  ['nps', 'NPS'],
  ['fixed_deposit', 'Fixed deposits'],
  ['gold', 'Gold'],
  ['bank_account', 'Bank accounts'],
  ['real_estate', 'Real estate'],
];

const REPORT_RESULT_COPY: Record<string, string> = {
  monthly_investments: 'Monthly cash movement and exact destinations for every recorded contribution.',
  total_summary: 'Money contributed in the selected period compared with current cost basis and the latest recorded value.',
  allocation: 'Current allocation and subcategory concentration across the selected investments.',
  holdings_accounts: 'Current holdings organized by broker, folio, or asset account.',
  performance: 'Recorded value, gain or loss, income, and portfolio XIRR where history is sufficient.',
  transaction_ledger: 'A dated, traceable ledger of cash and non-cash portfolio events.',
};

const money = (value: number | null | undefined) => value === null || value === undefined || !Number.isFinite(Number(value)) ? 'Unavailable' : new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0
}).format(Number(value));

const percent = (value: number | null | undefined) => value === null || value === undefined ? 'Unavailable' : `${Number(value).toFixed(2)}%`;

const ASSET_LABELS: Record<string, string> = {nps: 'NPS', ppf: 'PPF', epf: 'EPF', lic: 'LIC / Insurance'};
const humanize = (value: string) => ASSET_LABELS[value] || value.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

const contributionEvidence = (row: Record<string, any>) => {
  if (row.evidence_status === 'recorded') return `${row.period_cash_entries} dated transaction${row.period_cash_entries === 1 ? '' : 's'}`;
  if (row.evidence_status === 'no_activity_in_period') return `No dated activity in period${row.first_cash_date ? `; history starts ${row.first_cash_date}` : ''}`;
  return 'Balance only; transaction history required';
};

type MonthlyInflowRow = {
  month: string;
  monthLabel: string;
  account: string;
  accountId: string;
  instrument: string;
  assetClass: string;
  entries: number;
  amount: number;
};

const monthlyInflowRows = (transactions: Array<Record<string, any>>): MonthlyInflowRow[] => {
  const grouped = new Map<string, MonthlyInflowRow>();
  transactions
    .filter(transaction => transaction.category === 'new_investment' && !transaction.non_cash)
    .forEach(transaction => {
      const month = String(transaction.date || '').slice(0, 7);
      const accountId = String(transaction.account_id || 'unassigned');
      const instrument = transaction.instrument || 'Unspecified investment';
      const key = `${month}|${accountId}|${instrument}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.entries += 1;
        existing.amount += Number(transaction.amount || 0);
        return;
      }
      const monthDate = new Date(`${month}-01T00:00:00Z`);
      grouped.set(key, {
        month,
        monthLabel: Number.isNaN(monthDate.getTime())
          ? month
          : monthDate.toLocaleDateString('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' }),
        account: transaction.account || 'Unassigned account',
        accountId,
        instrument,
        assetClass: transaction.asset_class || 'unclassified',
        entries: 1,
        amount: Number(transaction.amount || 0),
      });
    });
  return Array.from(grouped.values()).sort((left, right) => (
    right.month.localeCompare(left.month)
    || left.account.localeCompare(right.account)
    || right.amount - left.amount
  ));
};

const reportGlyph = (type: string) => ({
  monthly_investments: 'MI', total_summary: 'TS', allocation: 'EN',
  holdings_accounts: 'HA', performance: 'PF', transaction_ledger: 'TL'
}[type] || 'RP');

const Reports: React.FC = () => {
  const [definitions, setDefinitions] = useState<ReportDefinition[]>([]);
  const [selectedType, setSelectedType] = useState(() => ['monthly_investments', 'total_summary', 'allocation', 'holdings_accounts', 'performance', 'transaction_ledger'].includes(new URLSearchParams(window.location.search).get('report') || '') ? new URLSearchParams(window.location.search).get('report')! : 'monthly_investments');
  const [filters, setFilters] = useState<ReportFilters>(initialFilters);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [recent, setRecent] = useState<GeneratedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [target, setTarget] = useState('');
  const [targetEditing, setTargetEditing] = useState(false);
  const [targetAllocation, setTargetAllocation] = useState<Record<string, number>>({});
  const [allocationEditing, setAllocationEditing] = useState(false);
  const [supportAccess, setSupportAccess] = useState<{ active: boolean; expires_at: string | null }>({ active: false, expires_at: null });
  const previewRef = useRef<HTMLDivElement>(null);
  const previewRequestRef = useRef(0);
  const focusPreviewRef = useRef(false);

  const loadRecent = async () => {
    const response = await axios.get(`${API_BASE_URL}/reports/`);
    setRecent(response.data);
    return response.data as GeneratedReport[];
  };

  useEffect(() => {
    let active = true;
    Promise.all([
      axios.get(`${API_BASE_URL}/reports/definitions/`),
      axios.get(`${API_BASE_URL}/reports/preferences/`),
      axios.get(`${API_BASE_URL}/reports/`),
      axios.get(`${API_BASE_URL}/reports/support-access/`),
    ]).then(([definitionResponse, preferenceResponse, recentResponse, supportResponse]) => {
      if (!active) return;
      setDefinitions(definitionResponse.data.reports);
      setTarget(preferenceResponse.data.monthly_investment_target ? String(preferenceResponse.data.monthly_investment_target) : '');
      setTargetAllocation(preferenceResponse.data.target_allocation || {});
      setRecent(recentResponse.data);
      setSupportAccess(supportResponse.data);
    }).catch(() => active && setError('Reports could not be loaded. Please try again.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const hasPending = recent.some(item => ['queued', 'processing'].includes(item.status));
    if (!hasPending) return;
    const timer = window.setInterval(() => loadRecent().catch(() => undefined), 3000);
    return () => window.clearInterval(timer);
  }, [recent]);

  const previewReport = async (scrollToResult = true) => {
    const requestId = ++previewRequestRef.current;
    setPreviewing(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/reports/preview/`, { report_type: selectedType, filters });
      if (requestId !== previewRequestRef.current) return;
      setSnapshot(response.data);
      if (scrollToResult || focusPreviewRef.current) {
        focusPreviewRef.current = false;
        window.setTimeout(() => previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      }
    } catch (err: any) {
      if (requestId === previewRequestRef.current) {
        setError(err.response?.data?.error || 'The report preview could not be generated.');
      }
    } finally {
      if (requestId === previewRequestRef.current) setPreviewing(false);
    }
  };

  useEffect(() => {
    if (loading || definitions.length === 0) return;
    const timer = window.setTimeout(() => previewReport(false), filters.instrument ? 400 : 120);
    return () => window.clearTimeout(timer);
    // The selected report and filter values are the complete preview query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, filters, loading, definitions.length]);

  const createExport = async () => {
    setExporting(true);
    setError('');
    try {
      await axios.post(`${API_BASE_URL}/reports/`, { report_type: selectedType, filters });
      await loadRecent();
    } catch (err: any) {
      setError(err.response?.data?.error || 'The report export could not be queued.');
    } finally {
      setExporting(false);
    }
  };

  const saveTarget = async () => {
    try {
      await axios.patch(`${API_BASE_URL}/reports/preferences/`, { monthly_investment_target: Number(target || 0) });
      setTargetEditing(false);
      if (snapshot) await previewReport();
    } catch (err: any) {
      setError(err.response?.data?.monthly_investment_target?.[0] || 'The target could not be saved.');
    }
  };

  const saveAllocationTargets = async () => {
    const cleaned = Object.fromEntries(Object.entries(targetAllocation).filter(([, value]) => Number(value) > 0));
    const total = Object.values(cleaned).reduce((sum, value) => sum + Number(value), 0);
    if (Math.abs(total - 100) > 0.01) {
      setError(`Target allocation must total 100%. It currently totals ${total.toFixed(2)}%.`);
      return;
    }
    try {
      await axios.patch(`${API_BASE_URL}/reports/preferences/`, { target_allocation: cleaned });
      setAllocationEditing(false);
      setError('');
      await previewReport();
    } catch (err: any) {
      setError(err.response?.data?.target_allocation?.[0] || 'Allocation targets could not be saved.');
    }
  };

  const deleteReport = async (id: string) => {
    await axios.delete(`${API_BASE_URL}/reports/${id}/`);
    setRecent(current => current.filter(report => report.id !== id));
  };

  const downloadReport = async (report: GeneratedReport, fileFormat: string) => {
    const response = await axios.get(`${API_BASE_URL}/reports/${report.id}/download/${fileFormat}/`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${report.report_type}-${report.id}.${fileFormat}`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleSupportAccess = async () => {
    if (supportAccess.active) {
      await axios.delete(`${API_BASE_URL}/reports/support-access/`);
      setSupportAccess({ active: false, expires_at: null });
      return;
    }
    const reason = window.prompt('Briefly tell support what you need help checking:');
    if (!reason) return;
    try {
      const response = await axios.post(`${API_BASE_URL}/reports/support-access/`, { reason });
      setSupportAccess(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Support access could not be changed.');
    }
  };

  const selected = definitions.find(item => item.type === selectedType);
  const details = snapshot?.report_type === 'transaction_ledger' ? snapshot.transactions : snapshot?.holdings || [];
  const accounts = snapshot ? Array.from(new Map(snapshot.holdings.map(item => [item.account_id, item.account])).entries()) : [];
  const inflowRows = snapshot ? monthlyInflowRows(snapshot.transactions) : [];
  const selectReport = (reportType: string) => {
    focusPreviewRef.current = true;
    setSnapshot(null);
    setSelectedType(reportType);
  };

  if (loading) return <div className="reports-loading"><span /> Preparing your reporting desk...</div>;

  return (
    <div className="reports-page">
      <header className="reports-hero">
        <div>
          <p className="reports-eyebrow">PORTFOLIO INTELLIGENCE</p>
          <h1>Reports</h1>
          <p>Review recorded contributions, allocation and performance across your accounts.</p>
        </div>
        <div className="reports-target-card">
          <span>Monthly investment target</span>
          {targetEditing ? (
            <div className="reports-target-edit">
              <span>Rs</span><input aria-label="Monthly investment target" type="number" min="0" value={target} onChange={event => setTarget(event.target.value)} />
              <button onClick={saveTarget}>Save</button>
            </div>
          ) : (
            <button className="reports-target-value" onClick={() => setTargetEditing(true)}>{target ? money(Number(target)) : 'Set a target'} <small>Edit</small></button>
          )}
          <small>Used only for progress and drift, never trade advice.</small>
        </div>
      </header>

      <section className="reports-catalogue" aria-label="Predefined reports">
        {definitions.map(definition => (
          <button key={definition.type} className={`report-choice report-choice--${definition.accent} ${selectedType === definition.type ? 'is-active' : ''}`} onClick={() => selectReport(definition.type)} aria-pressed={selectedType === definition.type}>
            <span className="report-choice__glyph">{reportGlyph(definition.type)}</span>
            <span><strong>{definition.title}</strong><small>{definition.description}</small></span>
            <span className="report-choice__arrow" aria-hidden="true">→</span>
          </button>
        ))}
      </section>

      <section className="reports-control-panel">
        <div className="reports-control-heading">
          <div><p>BUILD REPORT</p><h2>{selected?.title}</h2></div>
          <span className="reports-formats">{selected?.formats.map(item => item.toUpperCase()).join(' / ')}</span>
        </div>
        <div className="reports-filter-grid">
          <label>Period<select value={filters.preset} onChange={event => setFilters({ ...filters, preset: event.target.value })}>
            <option value="current_month">Current month</option><option value="last_12_months">Last 12 months</option>
            <option value="current_fy">Current financial year</option><option value="previous_fy">Previous financial year</option><option value="custom">Custom range</option>
          </select></label>
          {filters.preset === 'custom' && <><label>From<input type="date" value={filters.start_date || ''} onChange={event => setFilters({ ...filters, start_date: event.target.value })} /></label><label>To<input type="date" value={filters.end_date || ''} onChange={event => setFilters({ ...filters, end_date: event.target.value })} /></label></>}
        </div>
        <details className="reports-advanced"><summary>Advanced filters: accounts, assets and instruments</summary><div className="reports-filter-grid">
          <label>Asset class<select value={filters.asset_class} onChange={event => setFilters({ ...filters, asset_class: event.target.value })}>
            <option value="all">All asset classes</option><option value="equity">Equity</option><option value="non_equity">Non-equity</option>
            <option value="hybrid">Hybrid</option><option value="commodity">Commodity</option><option value="alternative">Alternative</option><option value="unclassified">Unclassified</option>
          </select></label>
          <label>Investment type<select value={filters.investment_type} onChange={event => setFilters({ ...filters, investment_type: event.target.value, account_id: 'all' })}>
            {INVESTMENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select></label>
          <label>Account<select value={filters.account_id} onChange={event => setFilters({ ...filters, account_id: event.target.value })}>
            <option value="all">All accounts</option>{accounts.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select></label>
          <label>Instrument<input aria-label="Search instrument" placeholder="Search name or fund" value={filters.instrument} onChange={event => setFilters({ ...filters, instrument: event.target.value })} /><small>Results refresh as you type</small></label>
          <label className="reports-checkbox"><input type="checkbox" checked={filters.include_closed} onChange={event => setFilters({ ...filters, include_closed: event.target.checked })} /> Include closed holdings</label>
        </div>
        </details>
        {error && <div className="reports-error" role="alert">{error}</div>}
        <div className="reports-actions"><span className="reports-auto-status">{previewing ? 'Refreshing result...' : 'Preview is up to date'}</span><button className="reports-button reports-button--primary" onClick={() => previewReport(true)} disabled={previewing}>{previewing ? 'Calculating...' : 'Refresh preview'}</button><button className="reports-button reports-button--secondary" onClick={createExport} disabled={exporting}>{exporting ? 'Queuing...' : 'Generate export'}</button></div>
      </section>

      {previewing && !snapshot && <section className="reports-preview-loading"><span />Building the {selected?.title?.toLowerCase()}...</section>}

      {snapshot && <section className="report-preview" ref={previewRef}>
        <div className="report-preview__header">
          <div><p>LIVE PREVIEW</p><h2>{snapshot.title}</h2><span className="report-date-range">{snapshot.filters.start_date} <i aria-hidden="true">to</i> {snapshot.filters.end_date}</span><small className="report-result-copy">{REPORT_RESULT_COPY[snapshot.report_type]}</small></div>
          <span className={`coverage-pill coverage-pill--${snapshot.coverage.level}`}>{humanize(snapshot.coverage.level)} coverage</span>
        </div>
        <div className={`coverage-banner coverage-banner--${snapshot.coverage.level}`}>
          <span className="coverage-banner__mark" aria-hidden="true">{snapshot.coverage.level === 'complete' ? 'OK' : '!'}</span>
          <div className="coverage-banner__body">
            <small>DATA CONFIDENCE</small>
            <strong>{snapshot.coverage.message}</strong><small className="coverage-banner__valuation">Values use stored stock quotes, recorded statement NAVs and asset snapshots. They may differ from the live Overview.</small>
            <div className="coverage-banner__facts">
              {snapshot.coverage.missing.length > 0 && <span>{snapshot.coverage.missing.length} data gap{snapshot.coverage.missing.length > 1 ? 's' : ''} identified</span>}
              {snapshot.coverage.unclassified_value > 0 && <span>{money(snapshot.coverage.unclassified_value)} unclassified</span>}
              {snapshot.coverage.missing.length === 0 && snapshot.coverage.unclassified_value === 0 && <span>All available records are classified</span>}
            </div>
          </div>
          {snapshot.coverage.missing.length > 0 && <Link className="coverage-action" to="/portfolio?tab=import"><span>Complete history</span><i aria-hidden="true">→</i></Link>}
        </div>


        {snapshot.report_type !== 'transaction_ledger' && <div className="report-metrics">
          <Metric label="Gross contributed" value={money(snapshot.summary.gross_contributions)} note="New money in selected period" />
          <Metric label="Recorded value" value={money(snapshot.summary.current_value)} note={`Cost basis ${money(snapshot.summary.current_cost_basis)}`} />
          <Metric label="Unrealized gain" value={money(snapshot.summary.unrealized_gain)} note={percent(snapshot.summary.unrealized_gain_percent)} tone={snapshot.summary.unrealized_gain >= 0 ? 'positive' : 'negative'} />
          {['performance', 'total_summary'].includes(snapshot.report_type) && <Metric
            label="Portfolio XIRR"
            value={percent(snapshot.summary.xirr)}
            note={snapshot.summary.xirr === null
              ? 'Unavailable: no matching dated history'
              : `Lifetime return on ${money(snapshot.summary.xirr_covered_value)} (${percent(snapshot.summary.xirr_coverage_percent)} coverage)`}
          />}
          {snapshot.report_type === 'monthly_investments' && <Metric label="Net cash flow" value={money(snapshot.summary.net_cash_flow)} note="Contributions less withdrawals in selected period" />}
          {snapshot.report_type === 'allocation' && <Metric label="Unclassified value" value={money(snapshot.coverage.unclassified_value)} note="Review classifications to improve allocation detail" />}
          {snapshot.report_type === 'holdings_accounts' && <Metric label="Open holdings" value={String(snapshot.holdings.filter(row => !row.closed).length)} note="Current holdings and asset accounts" />}
        </div>}

        {snapshot.report_type === 'monthly_investments' && <div className="reports-comparison-strip">
          <span><small>This month to date</small><strong>{money(snapshot.summary.current_month_investment)}</strong></span>
          <span><small>Previous full month</small><strong>{money(snapshot.summary.previous_month_investment)}</strong><em>{snapshot.summary.month_over_month_change === null ? 'No prior baseline' : `${snapshot.summary.month_over_month_change >= 0 ? '+' : ''}${percent(snapshot.summary.month_over_month_change)}`}</em></span>
          <span><small>Current FY to date</small><strong>{money(snapshot.summary.current_fy_investment)}</strong></span>
          <span><small>Previous full FY</small><strong>{money(snapshot.summary.previous_fy_investment)}</strong><em>{snapshot.summary.fy_over_fy_change === null ? 'No prior baseline' : `${snapshot.summary.fy_over_fy_change >= 0 ? '+' : ''}${percent(snapshot.summary.fy_over_fy_change)}`}</em></span>
        </div>}

        {['monthly_investments', 'allocation', 'total_summary'].includes(snapshot.report_type) && <div className="reports-visual-grid">
          {snapshot.report_type === 'monthly_investments' &&
          <div className="reports-chart-card reports-chart-card--wide"><div className="reports-chart-title"><div><small>CASH FLOW RHYTHM</small><h3>Monthly investment movement</h3></div><strong>{money(snapshot.summary.monthly_average)}<small> average</small></strong></div>
            <ResponsiveContainer width="100%" height={280}><BarChart data={snapshot.monthly}><CartesianGrid strokeDasharray="3 5" vertical={false} stroke="#dfe7e8" /><XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tickFormatter={(value) => `${Math.round(value / 1000)}k`} tick={{ fontSize: 11 }} /><Tooltip formatter={(value: any) => money(Number(value))} /><Legend /><Bar dataKey="invested" name="Invested" fill="#0d7c66" radius={[5, 5, 0, 0]} /><Bar dataKey="withdrawn" name="Withdrawn" fill="#ed6a3d" radius={[5, 5, 0, 0]} /></BarChart></ResponsiveContainer>
          </div>}
          {['allocation', 'total_summary'].includes(snapshot.report_type) &&
          <div className="reports-chart-card"><div className="reports-chart-title"><div><small>CURRENT MIX</small><h3>Asset allocation</h3></div><button className="allocation-edit-button" onClick={() => setAllocationEditing(value => !value)}>{allocationEditing ? 'Cancel' : 'Set targets'}</button></div>
            <ResponsiveContainer width="100%" height={215}><PieChart><Pie data={snapshot.allocation.filter(item => item.value > 0)} dataKey="value" nameKey="bucket" innerRadius={58} outerRadius={86} paddingAngle={3}>{snapshot.allocation.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}</Pie><Tooltip formatter={(value: any) => money(Number(value))} /></PieChart></ResponsiveContainer>
            {allocationEditing ? <div className="allocation-target-editor">{['equity', 'non_equity', 'hybrid', 'solution_oriented', 'commodity', 'alternative', 'unclassified'].map(bucket => <label key={bucket}><span>{humanize(bucket)}</span><input aria-label={`${humanize(bucket)} target`} type="number" min="0" max="100" value={targetAllocation[bucket] || ''} onChange={event => setTargetAllocation({ ...targetAllocation, [bucket]: Number(event.target.value) })} /><small>%</small></label>)}<button onClick={saveAllocationTargets}>Save 100% target</button></div> : <div className="allocation-legend">{snapshot.allocation.filter(item => item.value > 0).map((item, index) => <span key={item.bucket}><i style={{ background: COLORS[index % COLORS.length] }} />{humanize(item.bucket)} <strong>{percent(item.percentage)}{item.target_percentage > 0 && <small> target {percent(item.target_percentage)} / drift {item.drift_percentage > 0 ? '+' : ''}{percent(item.drift_percentage)}</small>}</strong></span>)}</div>}
          </div>}
        </div>}

        {['monthly_investments', 'total_summary'].includes(snapshot.report_type) && <div className="reports-detail-card reports-contribution-card"><div className="reports-chart-title"><div><small>DIVISION CONTRIBUTIONS</small><h3>Contribution by investment type</h3></div><span className={snapshot.reconciliation.matched ? 'reconciliation-ok' : 'reconciliation-error'}>{snapshot.reconciliation.matched ? 'Totals reconciled' : 'Review mismatch'}</span></div>
          <div className="reports-table-wrap"><table className="reports-contribution-table"><thead><tr><th>Investment type</th><th>Evidence</th><th>Contributed</th><th>Withdrawn</th><th>Net contribution</th><th>Recorded value</th><th>Share of money in</th></tr></thead>
            <tbody>{snapshot.contributions_by_type.map(row => <tr key={row.investment_type}><td><strong>{humanize(row.investment_type)}</strong></td><td><span className={`contribution-evidence contribution-evidence--${row.evidence_status}`}>{contributionEvidence(row)}</span></td><td>{row.evidence_status === 'balance_only' ? 'Unavailable' : money(row.contributed)}</td><td>{row.evidence_status === 'balance_only' ? 'Unavailable' : money(row.withdrawn)}</td><td>{row.evidence_status === 'balance_only' ? 'Unavailable' : money(row.net_contribution)}</td><td>{money(row.current_value)}</td><td>{row.evidence_status === 'balance_only' ? 'Unavailable' : percent(row.contribution_percentage)}</td></tr>)}</tbody></table>
            {snapshot.contributions_by_type.length === 0 && <div className="reports-empty">No recorded contributions match these filters.</div>}
          </div>
        </div>}

        {snapshot.report_type === 'allocation' && <div className="reports-detail-card"><div className="reports-chart-title"><div><small>ALLOCATION DETAIL</small><h3>Subcategories</h3></div><span>{snapshot.subcategories.length} categories</span></div>
          <div className="reports-table-wrap"><table><thead><tr><th>Asset class</th><th>Subcategory</th><th>Recorded value</th></tr></thead><tbody>{snapshot.subcategories.map(row => <tr key={`${row.bucket}-${row.subcategory}`}><td>{humanize(row.bucket)}</td><td><strong>{row.subcategory}</strong></td><td>{money(row.value)}</td></tr>)}</tbody></table></div>
        </div>}

        {snapshot.report_type === 'monthly_investments' && <div className="reports-detail-card reports-inflow-card"><div className="reports-chart-title"><div><small>CASH-IN DESTINATIONS</small><h3>Monthly inflow by account and fund</h3></div><span>{inflowRows.length} account/fund rows</span></div>
          <div className="reports-table-wrap"><table className="reports-inflow-table"><thead><tr><th>Month</th><th>Account</th><th>Fund / investment</th><th>Class</th><th>Entries</th><th>Money in</th></tr></thead>
            <tbody>{inflowRows.map(row => <tr key={`${row.month}-${row.accountId}-${row.instrument}`}><td><strong>{row.monthLabel}</strong></td><td>{row.account}</td><td><strong>{row.instrument}</strong></td><td>{humanize(row.assetClass)}</td><td>{row.entries}</td><td className="inflow-amount">{money(row.amount)}</td></tr>)}</tbody></table>
            {inflowRows.length === 0 && <div className="reports-empty">No recorded cash inflows match this period and the selected filters.</div>}
          </div>
        </div>}

        {['total_summary', 'holdings_accounts', 'performance', 'transaction_ledger'].includes(snapshot.report_type) && <div className={`reports-detail-card ${snapshot.report_type === 'performance' ? 'reports-performance-card' : ''}`}><div className="reports-chart-title"><div><small>TRACEABLE DETAIL</small><h3>{snapshot.report_type === 'transaction_ledger' ? 'Normalized transactions' : snapshot.report_type === 'performance' ? 'Performance by holding' : 'Holdings and accounts'}</h3></div><span className="reports-row-count">{details.length} rows</span></div>
          <div className="reports-table-wrap"><table><thead><tr>{snapshot.report_type === 'transaction_ledger' ? <><th>Date</th><th>Event</th><th>Instrument</th><th>Account</th><th>Amount</th><th>Class</th></> : <><th>Instrument</th><th>Class</th><th>Account</th><th>Cost basis</th><th>Recorded value</th><th>Gain/Loss</th></>}</tr></thead>
            <tbody>{details.slice(0, 100).map((row, index) => snapshot.report_type === 'transaction_ledger' ? <tr key={`${row.source_type}-${row.source_id}`}><td>{row.date}</td><td><span className={`event-tag event-tag--${row.category}`}>{humanize(row.category)}</span></td><td>{row.instrument}</td><td>{row.account}</td><td>{money(row.amount)}</td><td>{humanize(row.asset_class)}</td></tr> : <tr key={`${row.identifier}-${row.account_id}-${index}`}><td><strong>{row.instrument}</strong><small>{row.subcategory}</small></td><td>{humanize(row.asset_class)}</td><td>{row.account}</td><td>{money(row.cost_basis)}</td><td>{money(row.current_value)}</td><td className={row.gain_loss >= 0 ? 'value-positive' : 'value-negative'}>{money(row.gain_loss)}</td></tr>)}</tbody></table>{details.length === 0 && <div className="reports-empty">No recorded rows match these filters.</div>}</div>
        </div>}
        {(snapshot.coverage.warnings.length > 0 || snapshot.coverage.missing.length > 0) && <details className="reports-disclosure"><summary>Review data coverage details</summary>{snapshot.coverage.warnings.map(item => <p key={item}>{item}</p>)}{snapshot.coverage.missing.map(item => <p key={item}>Missing: {item}</p>)}</details>}
        <p className="reports-disclaimer">{snapshot.disclaimer}</p>
      </section>}

      <section className="recent-reports"><div className="recent-reports__heading"><div><p>PRIVATE ARCHIVE</p><h2>Recent reports</h2></div><div className="reports-privacy-controls"><span>Automatically removed after 30 days</span><button className={supportAccess.active ? 'is-active' : ''} onClick={toggleSupportAccess}>{supportAccess.active ? 'Revoke support access' : 'Grant 24h support access'}</button></div></div>
        {recent.length === 0 ? <div className="reports-empty reports-empty--archive">Generated exports will appear here with private download controls.</div> : <div className="recent-report-list">{recent.map(report => <div className="recent-report" key={report.id}><span className="recent-report__mark">{reportGlyph(report.report_type)}</span><div><strong>{report.title}</strong><small>{new Date(report.created_at).toLocaleString('en-IN')}</small></div><div className="recent-report__status"><span className={`status-dot status-dot--${report.status}`} />{humanize(report.status)}{['queued', 'processing'].includes(report.status) && <small>{report.progress}%</small>}{report.status === 'failed' && <small>{report.error_message}</small>}</div><div className="recent-report__actions">{report.available_formats.map(format => <button key={format} onClick={() => downloadReport(report, format)}>{format.toUpperCase()}</button>)}<button className="delete-report" onClick={() => deleteReport(report.id)}>Delete</button></div></div>)}</div>}
      </section>
    </div>
  );
};

const Metric: React.FC<{ label: string; value: string; note: string; tone?: string }> = ({ label, value, note, tone }) => <div className={`report-metric ${tone ? `report-metric--${tone}` : ''}`}><i aria-hidden="true" /><span>{label}</span><strong className={tone ? `value-${tone}` : ''}>{value}</strong><small>{note}</small></div>;

export default Reports;
